/**
 * WorkspaceModal.tsx
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Modal dialog for viewing and editing Workspace details.
 */

import React, { useEffect, useMemo } from 'react';
import { LayoutGrid, Cpu, Building2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Badge } from '../../components/ui';

import {
  EntityDetailModal,
  ReadOnlyField,
  EditableField,
  ChipTagInput,
} from '../../components/EntityDetailModal';

const Divider = () => <hr className="my-4 border-[var(--color-border)]" />;
import {
  useWorkspace,
  useUpdateWorkspace,
  useWorkspaceOwners,
  UpdateWorkspaceRequest,
} from '../../services/workspaceApi';

export interface WorkspaceModalProps {
  /** Whether modal is open */
  open: boolean;
  /** Workspace ID to display */
  workspaceId: string | null;
  /** Handler for modal close */
  onClose: () => void;
  /** Handler after successful save */
  onSaved?: () => void;
}

interface FormValues {
  display_name: string;
  owner_id: string;
  backup_priority: number;
  tags: string[];
}
// NOTE: `cost_center` and `notes` were removed from this form — dbo.workspaces
// has no columns for them, so editing them silently discarded input. Re-add them
// (here + FormValues + the PATCH payload) only after a migration adds the columns.

/**
 * WorkspaceModal - View and edit Workspace details
 */
export function WorkspaceModal({
  open,
  workspaceId,
  onClose,
  onSaved,
}: WorkspaceModalProps): React.ReactElement {
  const { enqueueSnackbar } = useSnackbar();

  // Fetch workspace data
  const {
    data: workspace,
    isLoading,
    error,
    refetch,
  } = useWorkspace(workspaceId);

  // Update mutation
  const updateMutation = useUpdateWorkspace();

  // Owner dropdown options — real users from GET /api/users
  const { data: owners } = useWorkspaceOwners();
  const ownerOptions = useMemo(
    () => [
      { value: '', label: 'Select owner…' },
      ...(owners ?? []).map((u) => ({
        value: u.id,
        label: u.display_name ?? u.email ?? u.id,
      })),
    ],
    [owners]
  );

  // Form setup
  const methods = useForm<FormValues>({
    defaultValues: {
      display_name: '',
      owner_id: '',
      backup_priority: 0,
      tags: [],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = methods;

  // Reset form when workspace data loads
  useEffect(() => {
    if (workspace) {
      reset({
        display_name: workspace.display_name ?? workspace.workspace_name ?? '',
        owner_id: workspace.owner_id ?? '',
        backup_priority: workspace.backup_priority ?? 0,
        tags: workspace.tags ?? [],
      });
    }
  }, [workspace, reset]);

  // Handle save
  const handleSave = async (data: FormValues) => {
    if (!workspaceId) return;

    try {
      const updateData: UpdateWorkspaceRequest = {
        display_name: data.display_name || undefined,
        owner_id: data.owner_id || undefined,
        backup_priority: data.backup_priority,
        tags: data.tags.length > 0 ? data.tags : undefined,
      };

      await updateMutation.mutateAsync({ id: workspaceId, data: updateData });

      enqueueSnackbar('Workspace updated successfully', { variant: 'success' });
      onSaved?.();
      onClose();
    } catch (err) {
      enqueueSnackbar('Failed to update workspace', { variant: 'error' });
    }
  };

  // Handle retry on error
  const handleRetry = () => {
    refetch();
  };

  // Read-only content (left column - Azure-managed)
  const readOnlyContent = (
    <div>
      <ReadOnlyField
        label="Workspace Name"
        value={workspace?.workspace_name}
        loading={isLoading}
        icon={<LayoutGrid className="h-4 w-4" />}
      />

      <ReadOnlyField
        label="Fabric Workspace ID"
        value={workspace?.fabric_workspace_id}
        loading={isLoading}
        tooltip="Microsoft Fabric Workspace identifier"
      />

      <ReadOnlyField
        label="Type"
        value={workspace?.type}
        loading={isLoading}
      />

      <ReadOnlyField
        label="State"
        value={
          workspace?.state && (
            <Badge variant={workspace.state === 'Active' ? 'success' : 'default'}>
              {workspace.state}
            </Badge>
          )
        }
        loading={isLoading}
      />

      {/*
        🛑 SPEC 268 A7 — THE "Status" FIELD IS REMOVED, AND IT SAT DIRECTLY BELOW THE
           FIELD THAT CONTRADICTED IT.

           `workspaces.status` is a DDL default with no writer: 629 of 629 production
           rows read 'Active', including all 111 whose `state` is 'Deleted'. So this
           modal rendered **State: Deleted** immediately above **Status: Active**, on
           111 workspaces, and had done since the column existed.

           The truth is the field left above — `state`/`api_state` are collector-written
           and genuinely vary (prod: 518 Active / 111 Deleted). Two fields where one is
           real and one is a constant is worse than one field, because the constant
           borrows credibility from its neighbour.

           (The removed badge also compared against lowercase 'active' while the stored
           value is 'Active', so its success variant could never apply — a second, minor
           wrongness that nobody noticed because the value was meaningless anyway.)

           Migration 269 drops the column; this render is removed first, per
           code-before-schema.
      */}

      <ReadOnlyField
        label="Description"
        value={workspace?.description}
        loading={isLoading}
      />

      <Divider />

      <ReadOnlyField
        label="Capacity"
        value={workspace?.capacity_name}
        loading={isLoading}
        icon={<Cpu className="h-4 w-4" />}
      />

      <ReadOnlyField
        label="Client"
        value={workspace?.client_name}
        loading={isLoading}
        icon={<Building2 className="h-4 w-4" />}
      />

      {/* Spec 298 FR-002: a retired client is never named as the client; it is shown here, with its retirement date */}
      {workspace?.retired_client_name && (
        <ReadOnlyField
          label="Retired client"
          value={workspace.retired_client_at
            ? `${workspace.retired_client_name} (retired ${new Date(workspace.retired_client_at).toISOString().slice(0, 10)})`
            : workspace.retired_client_name}
          loading={isLoading}
          icon={<Building2 className="h-4 w-4" />}
        />
      )}

      <ReadOnlyField
        label="Lakehouses"
        value={workspace?.lakehouse_count}
        loading={isLoading}
        tooltip="Number of lakehouses in this workspace"
      />

      <Divider />

      <ReadOnlyField
        label="Last Activity"
        value={workspace?.last_activity_at}
        loading={isLoading}
      />

      <ReadOnlyField
        label="Created"
        value={workspace?.created_at}
        loading={isLoading}
      />

      <ReadOnlyField
        label="Last Updated"
        value={workspace?.updated_at}
        loading={isLoading}
      />
    </div>
  );

  // Editable content (right column - Control Plane-managed)
  const editableContent = (
    <div>
      <EditableField
        name="display_name"
        control={control}
        label="Display Name"
        type="text"
        tooltip="Friendly name for this workspace"
        placeholder="Enter display name"
      />

      <EditableField
        name="owner_id"
        control={control}
        label="Owner"
        type="select"
        tooltip="Person responsible for this workspace"
        options={ownerOptions}
      />

      <Divider />

      <EditableField
        name="backup_priority"
        control={control}
        label="Backup Priority"
        type="number"
        min={0}
        max={100}
        tooltip="Higher priority workspaces are backed up first (0-100)"
      />

      <Divider />

      <ChipTagInput
        name="tags"
        control={control}
        label="Tags"
        tooltip="Add tags for categorization"
        placeholder="Type and press Enter to add tags"
        freeSolo
        maxItems={20}
      />
    </div>
  );

  return (
    <EntityDetailModal
      open={open}
      title={workspace?.display_name ?? workspace?.workspace_name ?? 'Workspace'}
      entityType="workspace"
      entityId={workspaceId ?? ''}
      onClose={onClose}
      onSave={handleSubmit(handleSave)}
      isDirty={isDirty}
      isSaving={updateMutation.isPending}
      error={error?.message}
      onRetry={handleRetry}
      readOnlyContent={readOnlyContent}
      editableContent={editableContent}
    />
  );
}

export default WorkspaceModal;
